import { IColumn, IObjectWithKey } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import AppContext from 'App/AppContext';
import { IPActionButton, IPText } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode, IPSelection } from 'baseComponents/Utils/Selection';
import Accordion from 'components/Accordion/Accordion';
import StringValuePicker from 'components/Pickers/StringValuePicker';
import { deleteIcon, globalStackTokensGapSmall, newIcon } from 'globalStyles';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

interface IStringValueMultiSelectProps {
  label: string;
  isReadOnly: boolean;
  selectedStringValues: StringValue[];
  stringValueType: StringValueTypes;
  addStringValues: (stringValues: StringValue[]) => void;
  removeStringValues: (stringValue: StringValue[]) => void;
  canCreateNew: boolean;
  info?: string;
}

const StringValueMultiSelect = (props: IStringValueMultiSelectProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'processingActivity', 'stringValue']);
  const addStringValueButtonId = useId('add-button');
  const [showStringValuePicker, setShowStringValuePicker] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<StringValue[]>([]);
  const [selection] = useState<IPSelection<IObjectWithKey>>(
    new IPSelection<IObjectWithKey>({
      selectionMode: IPSelectionMode.multiple,
      getKey: (item: IObjectWithKey) => {
        return (item as StringValue).stringValueId;
      },
      onSelectionChanged: () => {
        const sel = selection.getSelection() as StringValue[];
        setSelectedItems(sel);
      },
    }),
  );

  const columns: IColumn[] = [
    {
      key: 'value',
      name: t('stringValue:Column.Value'),
      minWidth: 200,
      maxWidth: 500,
      isResizable: true,
      onRender: (item?: StringValue, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{item.value}</IPText>;
      },
    },
    {
      key: 'description',
      name: t('stringValue:Column.Description'),
      minWidth: 200,
      maxWidth: 500,
      isResizable: true,
      isCollapsible: true,
      onRender: (item?: StringValue, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{item.description}</IPText>;
      },
    },
  ];

  const getLocalStateKey = (): LocalStorageKeys | undefined => {
    let key: LocalStorageKeys;
    switch (props.stringValueType) {
      case StringValueTypes.DataCategory:
        key = LocalStorageKeys.StringValueMultiSelectAccordionDataCategory;
        break;
      case StringValueTypes.LegalBasis:
        key = LocalStorageKeys.StringValueMultiSelectAccordionLegalBasis;
        break;
      case StringValueTypes.DataSubject:
        key = LocalStorageKeys.StringValueMultiSelectAccordionDataSubject;
        break;
      case StringValueTypes.ExecutingEntity:
        key = LocalStorageKeys.StringValueMultiSelectAccordionExecutingEntity;
        break;
      case StringValueTypes.RecipientCategory:
        key = LocalStorageKeys.StringValueMultiSelectAccordionRecipientCategory;
        break;
      case StringValueTypes.RetentionPeriod:
        key = LocalStorageKeys.StringValueMultiSelectAccordionRetentionPeriod;
        break;
      default:
        return undefined;
    }

    return key;
  };

  const getDefaultActiveFromLocalState = (): boolean => {
    const key = getLocalStateKey();
    if (!key) return false;
    const localState = getLocalStorageData(appContext, key);

    return localState === 'true';
  };

  const setDefaultActiveToLocalState = (expanded: boolean) => {
    const key = getLocalStateKey();
    if (!key) return;
    setLocalStorageData(appContext, key, expanded.toString());
  };

  //
  // Main render
  //
  return (
    <Accordion
      title={props.label}
      showBorder={true}
      summary={props.selectedStringValues ? props.selectedStringValues.length.toString() : ''}
      info={props.info}
      defaultActive={getDefaultActiveFromLocalState()}
      onToggle={(expanded) => setDefaultActiveToLocalState(expanded)}
    >
      <IPStack styles={{ root: { padding: 10 } }} tokens={globalStackTokensGapSmall}>
        {!props.isReadOnly && (
          <IPStack.Item>
            <IPActionButton
              iconProps={newIcon}
              id={addStringValueButtonId}
              onClick={() => setShowStringValuePicker(true)}
            >
              {t('translation:General.Button.Add')}
            </IPActionButton>
            <IPActionButton
              disabled={selectedItems.length === 0}
              iconProps={deleteIcon}
              onClick={() => props.removeStringValues(selectedItems)}
            >
              {t('translation:General.Button.Remove')}
            </IPActionButton>
            {showStringValuePicker && (
              <StringValuePicker
                isOpen={showStringValuePicker}
                target={`#${addStringValueButtonId}`}
                onDismiss={() => setShowStringValuePicker(false)}
                onSelect={(items) => props.addStringValues(items)}
                existingStringValues={props.selectedStringValues.map((sv) => sv.stringValueId)}
                stringValueType={props.stringValueType}
                canCreateNew={props.canCreateNew}
              />
            )}
          </IPStack.Item>
        )}
        {props.selectedStringValues.length > 0 && (
          <IPStack.Item>
            <IPDetailsList
              compact
              items={props.selectedStringValues}
              selectionPreservedOnEmptyClick={true}
              layoutMode={IPDetailsListLayoutMode.justified}
              columns={columns}
              selectionMode={IPSelectionMode.multiple}
              selection={selection}
              isHeaderVisible={false}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </Accordion>
  );
};

export default StringValueMultiSelect;
